import type { Row } from "@tanstack/react-table";
import { useVirtualizer, type Virtualizer } from "@tanstack/react-virtual";
import { useRef } from "react";
import { TABLE_CONFIG } from "./constants";

interface UseTableVirtualizationOptions<TData extends Record<string, unknown>> {
  /** TanStack Table instance (duck-typed via getRowModel) */
  table: { getRowModel: () => { rows: Row<TData>[] } };
  /** Enable row virtualisation */
  enabled?: boolean;
  /** Additional options forwarded to @tanstack/react-virtual useVirtualizer */
  options?: Record<string, unknown>;
}

/**
 * Hook for table row virtualisation.
 * Returns null virtualizer when disabled — avoids mounting unused DOM observers.
 */
export function useTableVirtualization<TData extends Record<string, unknown>>({
  table,
  enabled = false,
  options = {},
}: UseTableVirtualizationOptions<TData>): {
  virtualizer: Virtualizer<HTMLDivElement, Element> | null;
  parentRef: React.RefObject<HTMLDivElement | null>;
} {
  const parentRef = useRef<HTMLDivElement | null>(null);

  // Hook call is unconditional: `enabled` is forwarded to the virtualizer,
  // which skips observer mounting and measurement when disabled (virtual-core
  // `_willUpdate`/`getMeasurements`). count is 0 while disabled so the table's
  // row model is not computed.
  const virtualizer = useVirtualizer({
    count: enabled ? table.getRowModel().rows.length : 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => TABLE_CONFIG.DEFAULT_ROW_HEIGHT,
    overscan: TABLE_CONFIG.DEFAULT_OVERSCAN,
    ...options,
    enabled,
  });

  return { virtualizer: enabled ? virtualizer : null, parentRef };
}
